import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CopyPlus,
  Globe,
  LayoutTemplate,
  Loader2,
  Palette,
  Sparkles,
  Wand2,
  Wrench,
} from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  cloneAndAdaptPage,
  duplicatePage,
  isPersonalTemplatePage,
  savePage,
  usePageList,
  type PageSummary,
} from "@/hooks/use-pages";
import { useTemplateRegistry } from "@/hooks/use-template-registry";
import { TemplateBrowser } from "@/components/pages/template-browser";
import {
  applyCloneAdaptations,
  CloneAdaptWizard,
  type CloneAdaptSubmission,
} from "@/components/pages/clone-adapt-wizard";
import { BRAND_CONTRACT_PATH } from "@/lib/api-paths";
import { SiteManagerPanel, PageInventoryPanel } from "./sites-panels";
import {
  normalizeWorkspaceTab,
  normalizeSiteSlug,
  formatThemeStatus,
  formatShellStatus,
  formatBrandStatus,
  type WorkspaceTab,
  type SiteRecord,
  type BrandKitSummary,
} from "./sites-utils";

const START_OPTIONS = [
  {
    id: "tell-me",
    title: "Tell Me",
    description: "Use a guided AI brief and approve a real checkpoint before editing.",
    href: "/pages/new?mode=tell-me",
    icon: Sparkles,
  },
  {
    id: "pick-one",
    title: "Pick One",
    description: "Start from an industry template and turn it into an AI-ready draft brief.",
    href: "/pages/new?mode=pick-one",
    icon: LayoutTemplate,
  },
  {
    id: "build-it",
    title: "Build It",
    description: "Open the full editor directly when you already know the structure.",
    href: "/pages/new?mode=build-it",
    icon: Wrench,
  },
] as const;

type SitePublishTarget = "preview" | "live";
type SitePublishStatus = "queued" | "running" | "completed" | "partial_failure" | "failed" | null;

interface SitePublishServingContract {
  storage: "r2";
  cdn: "cloudflare_worker";
  worker: "page-serve";
  liveRoute: string;
  previewRoute: string;
  r2Prefix: string;
  sitemapKey: string;
  robotsTxtKey: string;
}

interface SitePublishCustomDomain {
  domainId: string;
  domain: string;
  status: string;
  sslStatus: string | null;
  dnsResolved: boolean | null;
  readyForRouting: boolean;
  liveUrl: string | null;
  healthEndpoint: string;
  verifyEndpoint: string;
  cnameTarget: string;
}

interface SitePublishState {
  target: SitePublishTarget;
  status: SitePublishStatus;
  jobId: string | null;
  totalPages: number;
  completedPages: number;
  failedPages: number;
  isSubmitting: boolean;
  error: string | null;
  liveUrl: string | null;
  servePath: string | null;
  previewUrl: string | null;
  scheduledPublishGap: string | null;
  serving: SitePublishServingContract | null;
  customDomain: SitePublishCustomDomain | null;
}

export default function SitesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pages, isLoading: isLoadingPages, error: pageError, refetch } = usePageList();
  const { templates, isLoading: isLoadingTemplates, error: templateError } = useTemplateRegistry();
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [brand, setBrand] = useState<BrandKitSummary | null>(null);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  const [activeReusePageId, setActiveReusePageId] = useState<string | null>(null);
  const [cloneWizardPage, setCloneWizardPage] = useState<PageSummary | null>(null);
  const [activeClonePageId, setActiveClonePageId] = useState<string | null>(null);
  const [cloneWizardError, setCloneWizardError] = useState<string | null>(null);
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteSlug, setNewSiteSlug] = useState("");
  const [sitePublishStates, setSitePublishStates] = useState<Record<string, SitePublishState>>({});

  const activeTab = normalizeWorkspaceTab(searchParams.get("tab"));
  const requestedSiteId = searchParams.get("siteId");
  const selectedSite = useMemo(() => {
    if (requestedSiteId) {
      return sites.find((site) => site.id === requestedSiteId) ?? sites[0] ?? null;
    }

    return sites[0] ?? null;
  }, [requestedSiteId, sites]);

  useEffect(() => {
    let isCancelled = false;

    async function loadWorkspace() {
      setIsLoadingSites(true);
      setSurfaceError(null);

      try {
        const [sitesRes, brandRes] = await Promise.all([
          apiFetch<{ data: SiteRecord[] }>("/crm/pages/sites"),
          apiFetch<{ data: BrandKitSummary | null }>(BRAND_CONTRACT_PATH).catch(() => ({ data: null })),
        ]);

        if (isCancelled) {
          return;
        }

        setSites(sitesRes.data ?? []);
        setBrand(brandRes.data ?? null);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setSurfaceError(error instanceof ApiError ? error.message : "Failed to load the page-builder workspace.");
        setSites([]);
      } finally {
        if (!isCancelled) {
          setIsLoadingSites(false);
        }
      }
    }

    void loadWorkspace();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedSite && sites.length === 0) {
      return;
    }

    if (!requestedSiteId && selectedSite) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("siteId", selectedSite.id);
      setSearchParams(nextParams, { replace: true });
    }
  }, [requestedSiteId, searchParams, selectedSite, setSearchParams, sites.length]);

  const homePageTitle = useMemo(() => {
    if (!selectedSite?.homePageId) {
      return null;
    }

    return pages.find((page) => page.id === selectedSite.homePageId)?.title ?? null;
  }, [pages, selectedSite]);

  const personalTemplates = useMemo(
    () => pages.filter((page) => isPersonalTemplatePage(page)),
    [pages],
  );
  const handleSaveAsTemplate = async (page: PageSummary) => {
    setActiveReusePageId(page.id);
    setSurfaceError(null);
    setSuccessMessage(null);

    try {
      const template = await duplicatePage({
        pageId: page.id,
        title: `${page.title} Template`,
        mode: "personal_template",
      });

      await refetch();
      setSuccessMessage(`${template.title} saved as a private personal template. Reapply it anytime from the template workspace or new-page flow.`);
      handleSelectTab("templates");
    } catch (error) {
      setSurfaceError(error instanceof ApiError ? error.message : "Failed to save page as a personal template.");
    } finally {
      setActiveReusePageId(null);
    }
  };

  const handleApplyPersonalTemplate = async (page: PageSummary) => {
    setActiveReusePageId(page.id);
    setSurfaceError(null);
    setSuccessMessage(null);

    try {
      const appliedPage = await duplicatePage({
        pageId: page.id,
        title: page.title.replace(/\s+Template$/i, ""),
        mode: "template_apply",
      });

      await refetch();
      navigate(`/pages/editor/${appliedPage.id}?entry=template`);
    } catch (error) {
      setSurfaceError(error instanceof ApiError ? error.message : "Failed to reapply the personal template.");
    } finally {
      setActiveReusePageId(null);
    }
  };

  const handleCloneForCampaign = (page: PageSummary) => {
    setCloneWizardPage(page);
    setCloneWizardError(null);
    setSurfaceError(null);
    setSuccessMessage(null);
  };

  const handleCloneAdaptSubmit = async (submission: CloneAdaptSubmission) => {
    if (!cloneWizardPage) {
      return;
    }

    setActiveClonePageId(cloneWizardPage.id);
    setCloneWizardError(null);

    try {
      const clonedPage = await cloneAndAdaptPage({
        pageId: cloneWizardPage.id,
        name: submission.name,
        slug: submission.slug,
      });
      const adaptation = applyCloneAdaptations(
        clonedPage.content,
        submission.name,
        submission.suggestions,
      );

      if (adaptation.changed) {
        await savePage(clonedPage.id, adaptation.content, "ai");
      }

      await refetch();
      setCloneWizardPage(null);
      navigate(`/pages/editor/${clonedPage.id}?entry=generated&source=clone-adapt`);
    } catch (error) {
      setCloneWizardError(error instanceof ApiError ? error.message : "Failed to clone and adapt the page.");
    } finally {
      setActiveClonePageId(null);
    }
  };

  const handleCreateSite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newSiteName.trim()) {
      return;
    }

    setIsCreatingSite(true);
    setCreateError(null);
    setSuccessMessage(null);

    try {
      const response = await apiFetch<{ data: SiteRecord }>("/crm/pages/sites", {
        method: "POST",
        body: JSON.stringify({
          name: newSiteName.trim(),
          slug: normalizeSiteSlug(newSiteSlug || newSiteName),
        }),
      });

      const createdSite = response.data;
      setSites((current) => [createdSite, ...current]);
      setNewSiteName("");
      setNewSiteSlug("");
      setSuccessMessage("Site container created. Next, choose a home page and global styles.");

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("tab", "sites");
      nextParams.set("siteId", createdSite.id);
      setSearchParams(nextParams);
    } catch (error) {
      setCreateError(error instanceof ApiError ? error.message : "Failed to create site.");
    } finally {
      setIsCreatingSite(false);
    }
  };

  const handleSelectTab = (tab: WorkspaceTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    if (selectedSite) {
      nextParams.set("siteId", selectedSite.id);
    }
    setSearchParams(nextParams);
  };

  const handleSelectSite = (siteId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("siteId", siteId);
    nextParams.set("tab", activeTab);
    setSearchParams(nextParams);
  };

  const pollSiteJob = async (siteId: string, jobId: string) => {
    try {
      const response = await apiFetch<{ data: {
        id: string;
        status: SitePublishStatus;
        totalPages: number;
        completedPages: number;
        failedPages: number;
      } }>(`/crm/pages/sites/${siteId}/publish/${jobId}`);

      setSitePublishStates((current) => {
        const existing = current[siteId];
        if (!existing || existing.jobId !== jobId) {
          return current;
        }

        return {
          ...current,
          [siteId]: {
            ...existing,
            status: response.data.status,
            totalPages: response.data.totalPages,
            completedPages: response.data.completedPages,
            failedPages: response.data.failedPages,
            isSubmitting: false,
            error: null,
          },
        };
      });
    } catch (error) {
      setSitePublishStates((current) => {
        const existing = current[siteId];
        if (!existing || existing.jobId !== jobId) {
          return current;
        }

        return {
          ...current,
          [siteId]: {
            ...existing,
            isSubmitting: false,
            error: error instanceof ApiError ? error.message : "Failed to refresh publish status.",
          },
        };
      });
    }
  };

  const handleSitePublishAction = async (siteId: string, target: SitePublishTarget) => {
    setSitePublishStates((current) => ({
      ...current,
      [siteId]: {
        target,
        status: null,
        jobId: null,
        totalPages: 0,
        completedPages: 0,
        failedPages: 0,
        isSubmitting: true,
        error: null,
        liveUrl: current[siteId]?.liveUrl ?? null,
        servePath: current[siteId]?.servePath ?? null,
        previewUrl: current[siteId]?.previewUrl ?? null,
        scheduledPublishGap: null,
        serving: current[siteId]?.serving ?? null,
        customDomain: current[siteId]?.customDomain ?? null,
      },
    }));

    try {
      const response = await apiFetch<{ data: {
        jobId: string;
        totalPages: number;
        liveUrl?: string;
        servePath?: string;
        previewUrl?: string;
        scheduledPublishGap?: string;
        serving?: SitePublishServingContract;
        customDomain?: SitePublishCustomDomain | null;
      } }>(
        `/crm/pages/sites/${siteId}/${target === "preview" ? "preview" : "publish"}`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
      );

      setSitePublishStates((current) => ({
        ...current,
        [siteId]: {
          target,
          status: "queued",
          jobId: response.data.jobId,
          totalPages: response.data.totalPages,
          completedPages: 0,
          failedPages: 0,
          isSubmitting: false,
          error: null,
          liveUrl: response.data.liveUrl ?? current[siteId]?.liveUrl ?? null,
          servePath: response.data.servePath ?? current[siteId]?.servePath ?? null,
          previewUrl: response.data.previewUrl ?? current[siteId]?.previewUrl ?? null,
          scheduledPublishGap: response.data.scheduledPublishGap ?? null,
          serving: response.data.serving ?? current[siteId]?.serving ?? null,
          customDomain: response.data.customDomain ?? current[siteId]?.customDomain ?? null,
        },
      }));

      await pollSiteJob(siteId, response.data.jobId);
    } catch (error) {
      setSitePublishStates((current) => ({
        ...current,
        [siteId]: {
          target,
          status: "failed",
          jobId: null,
          totalPages: 0,
          completedPages: 0,
          failedPages: 0,
          isSubmitting: false,
          error: error instanceof ApiError ? error.message : `Failed to ${target === "preview" ? "create preview" : "publish site"}.`,
          liveUrl: current[siteId]?.liveUrl ?? null,
          servePath: current[siteId]?.servePath ?? null,
          previewUrl: current[siteId]?.previewUrl ?? null,
          scheduledPublishGap: null,
          serving: current[siteId]?.serving ?? null,
          customDomain: current[siteId]?.customDomain ?? null,
        },
      }));
    }
  };

  useEffect(() => {
    const activeJobs = Object.entries(sitePublishStates).filter(([, state]) =>
      state.jobId && (state.status === "queued" || state.status === "running"),
    );

    if (activeJobs.length === 0) {
      return;
    }

    const intervalId = setInterval(() => {
      activeJobs.forEach(([siteId, state]) => {
        if (state.jobId) {
          void pollSiteJob(siteId, state.jobId);
        }
      });
    }, 2500);

    return () => {
      clearInterval(intervalId);
    };
  }, [sitePublishStates]);

  if (!isLoadingSites && surfaceError && sites.length === 0) {
    return (
      <div className="space-y-6">
        <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl space-y-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-300">
                <Wand2 className="h-3.5 w-3.5" />
                Page Builder Shell
              </span>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground">Tell Me, Pick One, or Build It.</h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                The mounted builder workspace did not load, so this shell is staying explicit instead of pretending there are no sites or templates.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-[24px] border border-destructive/25 bg-destructive/5 p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Workspace unavailable</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  The site inventory request failed before any mounted data arrived. Retry the load before acting on this workspace.
                </p>
              </div>
              <div className="rounded-2xl border border-destructive/20 bg-background/80 px-4 py-3 text-sm text-destructive">
                {surfaceError}
              </div>
              <button
                type="button"
                onClick={() => navigate(0)}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                Retry workspace load
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-300">
              <Wand2 className="h-3.5 w-3.5" />
              Page Builder Shell
            </span>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Tell Me, Pick One, or Build It.</h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              This workspace keeps site setup, template direction, and the full editor in one honest flow. Start guided, start from a template, or jump into the builder only when you are ready.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {START_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <Link
                  key={option.id}
                  to={option.href}
                  className="rounded-2xl border border-border bg-background px-4 py-3 text-sm transition hover:border-foreground/20 hover:bg-accent"
                >
                  <span className="mb-1 flex items-center gap-2 font-medium text-foreground">
                    <Icon className="h-4 w-4" />
                    {option.title}
                  </span>
                  <span className="text-muted-foreground">{option.description}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {surfaceError ? (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{surfaceError}</span>
        </div>
      ) : null}

      {pageError ? (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Page inventory is unavailable right now. Site containers still load, but page-linked details such as home-page titles may be incomplete until `/crm/pages` recovers. {pageError}
          </span>
        </div>
      ) : null}

      {successMessage ? (
        <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{successMessage}</span>
        </div>
      ) : null}

      <section className="grid gap-4 xl:grid-cols-[1.25fr_0.75fr]">
        <article className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Workspace</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage sites, inspect the current page inventory, and move into templates without losing the mounted contracts.
              </p>
            </div>

            <div className="flex rounded-xl border border-border bg-background p-1 text-sm">
              {(["sites", "templates", "pages"] as WorkspaceTab[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleSelectTab(tab)}
                  className={`rounded-lg px-3 py-1.5 capitalize transition ${
                    activeTab === tab ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            {activeTab === "sites" ? (
              <SiteManagerPanel
                sites={sites}
                pages={pages}
                isLoading={isLoadingSites}
                selectedSiteId={selectedSite?.id ?? null}
                onSelectSite={handleSelectSite}
                onCreateSite={handleCreateSite}
                createError={createError}
                isCreatingSite={isCreatingSite}
                newSiteName={newSiteName}
                newSiteSlug={newSiteSlug}
                onNameChange={setNewSiteName}
                onSlugChange={setNewSiteSlug}
                onOpenSettings={(siteId) => navigate(`/pages/settings?siteId=${siteId}`)}
                onEditShell={(siteId, shell) => navigate(`/pages/editor/new?siteId=${siteId}&shell=${shell}`)}
                onEditNavigation={(siteId) => navigate(`/pages/settings?siteId=${siteId}&panel=navigation`)}
                onCreateWithAi={(siteId) => navigate(`/pages/ai-generate?siteId=${siteId}`)}
                onBuildBlank={(siteId) => navigate(`/pages/editor/new?siteId=${siteId}`)}
                onPreviewSite={(siteId) => void handleSitePublishAction(siteId, "preview")}
                onPublishSite={(siteId) => void handleSitePublishAction(siteId, "live")}
                publishStates={sitePublishStates}
              />
            ) : null}

            {activeTab === "templates" ? (
              <div className="space-y-5">
                <article className="rounded-[22px] border border-border bg-background p-5 shadow-sm">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Private reuse</p>
                      <h3 className="mt-2 text-lg font-semibold text-foreground">Personal templates stay inside your tenant.</h3>
                      <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                        Save any existing page as a personal template, then reapply it through the same duplicate-page model used for ordinary copies. Public marketplace publishing remains a separate snapshot listing flow.
                      </p>
                    </div>
                    <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      {personalTemplates.length} personal templates
                    </div>
                  </div>

                  {personalTemplates.length === 0 ? (
                    <p className="mt-4 text-sm leading-6 text-muted-foreground">
                      No personal templates saved yet. Use the page inventory tab to duplicate a page into a reusable private template.
                    </p>
                  ) : (
                    <div className="mt-5 grid gap-4 lg:grid-cols-2">
                      {personalTemplates.map((page) => (
                        <article key={page.id} className="rounded-[20px] border border-border bg-card p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-base font-semibold text-foreground">{page.title}</p>
                              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">private template · /{page.slug}</p>
                            </div>
                            <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                              updated {new Date(page.updatedAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="mt-4 flex flex-wrap gap-3">
                            <button
                              type="button"
                              onClick={() => void handleApplyPersonalTemplate(page)}
                              disabled={activeReusePageId === page.id}
                              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
                            >
                              {activeReusePageId === page.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CopyPlus className="h-4 w-4" />}
                              Reapply template
                            </button>
                            <span className="inline-flex items-center rounded-xl border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                              Not listed in marketplace
                            </span>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </article>

                <article className="rounded-[22px] border border-border bg-background p-5 shadow-sm">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Starter registry</p>
                      <h3 className="mt-2 text-lg font-semibold text-foreground">Canonical vertical starters now come from the shared builder registry.</h3>
                      <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                        This workspace now reads starter templates from the shared page-builder registry instead of the deprecated app-local template metadata file.
                      </p>
                    </div>
                    <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      {templates.length} shared starter templates
                    </div>
                  </div>

                  <div className="mt-5">
                    <TemplateBrowser
                      templates={templates}
                      isLoading={isLoadingTemplates}
                      error={templateError}
                      siteId={selectedSite?.id ?? null}
                    />
                  </div>
                </article>
              </div>
            ) : null}

            {activeTab === "pages" ? (
              <PageInventoryPanel
                isLoading={isLoadingPages}
                pageError={pageError}
                pages={selectedSite ? pages.filter((page) => page.siteId === selectedSite.id) : pages}
                selectedSite={selectedSite ? { id: selectedSite.id, name: selectedSite.name } : null}
                onRetry={refetch}
                onSaveAsTemplate={(page) => void handleSaveAsTemplate(page)}
                onCloneForCampaign={handleCloneForCampaign}
                isApplyingToTemplate={(pageId) => activeReusePageId === pageId}
                isCloningForCampaign={(pageId) => activeClonePageId === pageId}
              />
            ) : null}
          </div>
        </article>

        <article className="space-y-4 rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Selected site</p>
            <h2 className="mt-2 text-xl font-semibold text-foreground">{selectedSite?.name ?? "No site yet"}</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              {selectedSite
                ? `/${selectedSite.slug} · ${selectedSite.siteType} · ${selectedSite.status}`
                : "Create a site container first if you want home-page assignment, global theme tokens, and publishing settings."}
            </p>
          </div>

          <div className="rounded-[22px] border border-border bg-background p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Palette className="h-4 w-4 text-primary" />
              Theme and brand entry points
            </div>
            <div className="mt-4 space-y-4 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">Site theme</p>
                <p className="mt-1 leading-6">
                  {selectedSite
                    ? formatThemeStatus(selectedSite.globalTheme)
                    : "No site selected yet. Create a site to store global colors, fonts, radius, spacing, and publishing scripts."}
                </p>
              </div>
              <div>
                <p className="font-medium text-foreground">Brand kit</p>
                <p className="mt-1 leading-6">
                  {brand
                    ? formatBrandStatus(brand)
                    : "Brand kit is managed through the real shared settings surface. If nothing is configured yet, start there before polishing site-level theme tokens."}
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              {selectedSite ? (
                <Link
                  to={`/pages/theme?siteId=${selectedSite.id}`}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                >
                  Theme and site settings
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
              {selectedSite ? (
                <Link
                  to={`/pages/theme?entry=pick-one&siteId=${selectedSite.id}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                >
                  Pick One quick customize
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
              <Link
                to="/settings/brand"
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
              >
                Open brand kit
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="rounded-[22px] border border-border bg-background p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Globe className="h-4 w-4 text-primary" />
              Current transition into the editor
            </div>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
              <li>Tell Me: checkpoint review first, editor second.</li>
              <li>Pick One: template direction first, AI draft second, editor after approval.</li>
              <li>Build It: blank editor immediately when you want full manual control.</li>
              <li>Personal templates: duplicate a real page, keep it private, then reapply it through the same mounted page contract.</li>
            </ul>
          </div>

          {selectedSite ? (
            <div className="rounded-[22px] border border-border bg-background p-5">
              <p className="text-sm font-medium text-foreground">Home page</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {homePageTitle
                  ? `${homePageTitle} is currently assigned as the home page.`
                  : selectedSite.homePageId && pageError
                    ? "A home page is assigned, but its title is unavailable because the page inventory failed to load."
                  : "No home page is assigned yet. Set one from theme and site settings when you know which draft should be the public entry page."}
              </p>
            </div>
          ) : null}

          {selectedSite ? (
            <div className="rounded-[22px] border border-border bg-background p-5">
              <p className="text-sm font-medium text-foreground">Site shell</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {formatShellStatus(selectedSite)}
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to={`/pages/editor/new?siteId=${selectedSite.id}&shell=header`}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                >
                  Edit global header
                </Link>
                <Link
                  to={`/pages/editor/new?siteId=${selectedSite.id}&shell=footer`}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                >
                  Edit global footer
                </Link>
              </div>
            </div>
          ) : null}
        </article>
      </section>

      <CloneAdaptWizard
        page={cloneWizardPage}
        isOpen={Boolean(cloneWizardPage)}
        isSubmitting={Boolean(activeClonePageId)}
        error={cloneWizardError}
        onClose={() => {
          if (!activeClonePageId) {
            setCloneWizardPage(null);
            setCloneWizardError(null);
          }
        }}
        onSubmit={handleCloneAdaptSubmit}
      />
    </div>
  );
}
